/**
 * Adapter Store → Core-AssessmentTexts.
 *
 * AP10: Der Berater pflegt Bewertungstexte im AssessmentEditor; dieser
 * Adapter wandelt den Store-Slice ins Format, das `buildStatementPlaceholders`
 * erwartet. Wenn Pflicht-Texte fehlen, returnen wir `null` — die UI muss
 * dann zwischen „Demo-Daten" und „eigene Texte" wählen.
 *
 * Pflicht-Felder = die 6 Top-Level-Texte aus `validate.ts` plus mindestens
 * ein Detail-Text pro Dimension. Andere Felder sind optional.
 */

import type { statement } from '@tragfaehigkeit/core';
import type { AssessmentDraft } from '../store/wizard';

const REQUIRED_SCALAR_FIELDS = [
  'verdictLongParagraph',
  'verdictReasoning',
  'recommendationDetailed',
  'constraintDiscussion',
  'riskIndicators',
  'strengthIndicators',
] as const;

export interface AssessmentReadiness {
  readonly isReady: boolean;
  readonly missingScalars: ReadonlyArray<string>;
  readonly missingDimensionDetails: ReadonlyArray<string>;
}

export function checkAssessmentReadiness(draft: AssessmentDraft): AssessmentReadiness {
  const missingScalars = REQUIRED_SCALAR_FIELDS.filter((k) => draft[k].trim().length === 0);
  const missingDimensionDetails = (
    Object.keys(draft.dimensionTexts) as Array<keyof typeof draft.dimensionTexts>
  ).filter((d) => draft.dimensionTexts[d].detail.trim().length === 0);
  return {
    isReady: missingScalars.length === 0 && missingDimensionDetails.length === 0,
    missingScalars,
    missingDimensionDetails,
  };
}

/**
 * Übersetzt einen vollständigen Draft ins Core-AssessmentTexts-Format.
 * `null` zurück, wenn Pflicht-Felder fehlen — Aufrufer entscheidet, wie
 * damit umzugehen ist (Demo-Daten? Generierung blockieren?).
 */
export function selectAssessmentsForCase(draft: AssessmentDraft): statement.AssessmentTexts | null {
  const readiness = checkAssessmentReadiness(draft);
  if (!readiness.isReady) return null;

  // Pflichtfelder werden direkt übernommen.
  const base: statement.AssessmentTexts = {
    verdictLongParagraph: draft.verdictLongParagraph,
    verdictReasoning: draft.verdictReasoning,
    recommendationDetailed: draft.recommendationDetailed,
    constraintDiscussion: draft.constraintDiscussion,
    riskIndicators: draft.riskIndicators,
    strengthIndicators: draft.strengthIndicators,
    dimensionTexts: draft.dimensionTexts,
  };

  // Optionale Felder werden nur gesetzt, wenn sie befüllt sind — sonst
  // weglassen, damit `exactOptionalPropertyTypes` keinen `undefined`-Konflikt
  // erzwingt.
  const optional: Record<string, string> = {};
  const optionalKeys = [
    'revenueAssessment',
    'costsAssessment',
    'profitAssessment',
    'hourlyRateAssessment',
    'liquidityAssessment',
    'runwayAssessment',
    'utilizationAssessment',
    'gapInterpretation',
    'conditionsParagraph',
    'reevaluationProposal',
  ] as const;
  for (const key of optionalKeys) {
    const value = draft[key];
    if (value.trim().length > 0) optional[key] = value;
  }

  return { ...base, ...optional };
}
